/**
 * Pont de bois (author's map, not in the GDD): a long humpbacked bridge of wood over the sea, from headland to
 * headland, on arches and posts, between piers of rock that carry a gate. The wood burns, and whoever stands on it
 * falls into the sea (head under: drowned). The rock stops the fire, so each span burns on its own, and a pier
 * stands on a shoal: a fall right next to it is survived, and its steep sides can be walked up.
 */
import { Physique, nextInt, sinDeg, TRIG_ONE } from '@invaders/sim';
import { plainMap, type MapState } from '../state';
import { type Gen, island, layer, rect, rootedTree, sea, settle, waves } from './common';

const { ROCK, SAND, WOOD } = Physique;

/** Half width of the top of a pier, and how many cells of height its sides lose per cell (a walkable slope). */
const PIER_HALF = 24;
const PIER_SLOPE = 4;
/** The shoal around a pier: its depth, and its width beyond the foot of the pier. */
const SHOAL_DEPTH = 6;
const SHOAL_WIDTH = 6;
/** The open sea under the bridge. */
const DEEP = 70;
const DECK_THICK = 4;
const POST_EVERY = 14;
/** The gate on a pier: its opening, and the height of its top (a double jump from the deck). */
const GATE_HALF = 14;
const GATE_HEIGHT = 22;
const GATE_TOP = 32;

export function generateBridge(g: Gen): MapState {
  const { w, rng, seaY } = g;
  const coastL = Math.trunc((w * 6) / 100) + nextInt(rng, 13) - 6;
  const coastR = Math.trunc((w * 94) / 100) + nextInt(rng, 13) - 6;
  const deck = 58 + nextInt(rng, 13); // rows above the sea
  const deckY = seaY - deck;

  // The two abutments, and the piers between them, not quite evenly spaced.
  const abutL = coastL + 84 + nextInt(rng, 16);
  const abutR = coastR - 84 - nextInt(rng, 16);
  const spans = 3 + nextInt(rng, 2);
  const piers: number[] = [];
  for (let k = 1; k < spans; k++) piers.push(abutL + Math.trunc(((abutR - abutL) * k) / spans) + nextInt(rng, 33) - 16);

  /** Height above the sea of a side of a pier, `d` cells beyond its flat top. */
  const side = (d: number) => {
    const shoal = Math.trunc(deck / PIER_SLOPE) + SHOAL_WIDTH;
    if (d <= shoal) return Math.max(deck - d * PIER_SLOPE, -SHOAL_DEPTH);
    return -SHOAL_DEPTH - (d - shoal) * PIER_SLOPE;
  };
  const floor = waves(g, 5, 3, 3, 11, 0, 1);
  const relief = waves(g, 7, 9, 3, 23, 0, 1);
  const elevation = (x: number) => {
    let e = -DEEP + floor(x);
    if (x <= abutL) return deck + Math.trunc((relief(x) * Math.min(40, abutL - x)) / 40);
    if (x >= abutR) return deck + Math.trunc((relief(x) * Math.min(40, x - abutR)) / 40);
    e = Math.max(e, side(x - abutL), side(abutR - x));
    for (const p of piers) e = Math.max(e, side(Math.abs(x - p) - PIER_HALF));
    return Math.min(e, deck);
  };
  const surface = island(g, coastL, coastR, 60, elevation);
  const rock = surface.slice();

  const ends = [abutL, ...piers.flatMap((p) => [p - PIER_HALF, p + PIER_HALF]), abutR];
  for (let k = 0; k < ends.length; k += 2) drawSpan(g, ends[k], ends[k + 1], deckY, 9 + nextInt(rng, 7));
  for (const p of piers) drawGate(g, p, deckY);

  // Sand and a few trees on the headlands, thinning out towards the bridge, which they do not touch.
  const beach = (toBridge: number) => Math.min(5, Math.trunc(Math.max(0, toBridge - 16) / 5));
  layer(g, surface, coastL - 40, abutL, (x) => beach(abutL - x), SAND);
  layer(g, surface, abutR, coastR + 40, (x) => beach(x - abutR), SAND);
  for (const [x0, x1] of [[coastL + 44, abutL - 36], [abutR + 36, coastR - 44]]) {
    for (let x = x0 + nextInt(rng, 10); x < x1; x += 24 + nextInt(rng, 16)) rootedTree(g, x, rock[x], surface[x], 24 + nextInt(rng, 16));
  }

  sea(g);
  settle(g);
  return plainMap('bridge');
}

/** One span of wood between two columns: a deck rising by `hump` in the middle, an arch under it, posts between them. */
function drawSpan(g: Gen, x0: number, x1: number, deckY: number, hump: number): void {
  const { rng } = g;
  const n = x1 - x0;
  const under = g.seaY - 10; // the feet of the arch
  const sine = (x: number) => sinDeg(Math.trunc(((x - x0) * 180) / n));
  const top = (x: number) => deckY - Math.trunc((hump * sine(x)) / TRIG_ONE);
  const arch = (x: number) => {
    const y = top(x) + DECK_THICK;
    return y + Math.trunc(((under - y) * (TRIG_ONE - sine(x))) / TRIG_ONE);
  };
  let last = arch(x0);
  for (let x = x0; x <= x1; x++) {
    rect(g, x, top(x), x, top(x) + DECK_THICK - 1, WOOD, 0);
    const y = arch(x);
    rect(g, x, Math.min(y, last), x, Math.max(y, last) + 2, WOOD, 0); // joined to the last column: the fire passes
    last = y;
    if ((x - x0) % POST_EVERY === 0) rect(g, x, top(x) + DECK_THICK, x + 1, y, WOOD, 0);
  }
  // A crate or two on the deck: some cover, and it burns.
  for (let k = 0, crates = 1 + nextInt(rng, 2); k < crates; k++) {
    const x = x0 + 24 + nextInt(rng, n - 64);
    const size = 10 + nextInt(rng, 7);
    rect(g, x, top(x) - size, x + size, top(x + size) + 1, WOOD, 0);
  }
}

/** A gate of rock on a pier, as a firebreak and a cover: two legs and a lintel with battlements. */
function drawGate(g: Gen, x: number, deckY: number): void {
  rect(g, x - PIER_HALF, deckY - GATE_TOP, x + PIER_HALF, deckY - 1, ROCK);
  rect(g, x - GATE_HALF, deckY - GATE_HEIGHT, x + GATE_HALF, deckY - 1, 0);
  for (let c = x - PIER_HALF; c <= x + PIER_HALF - 5; c += 10) rect(g, c, deckY - GATE_TOP - 4, c + 5, deckY - GATE_TOP - 1, ROCK);
}
